import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { API_URL } from '../utils/Functions';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

window.Quill = Quill;
const ImageResize = require('quill-image-resize-module-react').default;

// Register custom fonts
const Font = Quill.import('formats/font');
Font.whitelist = ['sans-serif', 'inter', 'outfit', 'roboto', 'playfair', 'merriweather', 'monospace'];
Quill.register(Font, true);

// Register font sizes
const Size = Quill.import('formats/size');
Size.whitelist = ['10px','12px','14px','16px','18px','20px','24px','28px','32px','36px','48px','64px'];
Quill.register(Size, true);

// Register image resize module
Quill.register('modules/imageResize', ImageResize, true);

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }, { font: Font.whitelist }, { size: Size.whitelist }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    [{ align: [] }],
    ['link', 'image', 'video'],
    ['blockquote', 'code-block', 'clean'],
  ],
  history: { delay: 500, maxStack: 100, userOnly: true },
  imageResize: {
    parchment: Quill.import('parchment'),
    modules: ['Resize', 'DisplaySize', 'Toolbar'],
  },
};

const AdminBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    is_published: true
  });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const toSlug = (str) => str.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isHtmlMode, setIsHtmlMode] = useState(false);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/blogs`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      const res = await response.json();
      if (!response.ok) throw res;
      
      // Handle different API response structures (e.g., paginated data vs flat array)
      let blogsArray = [];
      if (Array.isArray(res)) {
        blogsArray = res;
      } else if (res && Array.isArray(res.data)) {
        blogsArray = res.data;
      } else if (res && res.data && Array.isArray(res.data.data)) {
        blogsArray = res.data.data;
      }
      
      setBlogs(blogsArray);
    } catch (err) {
      // In case the API is not yet implemented or fails, we will show an empty list or error
      // But we will allow the UI to function for display purposes if API is down
      setError('Failed to load blogs (Ensure API is configured).');
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleOpenCreate = () => {
    setEditingItem(null);
    setImageFile(null);
    setImagePreview(null);
    setSlugManuallyEdited(false);
    setIsHtmlMode(false);
    setFormData({
      title: '',
      slug: '',
      content: '',
      is_published: true
    });
    setShowFormModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditingItem(item);
    setImageFile(null);
    setImagePreview(item.image || item.img || null);
    setSlugManuallyEdited(true); // Don't auto-override existing slug on edit
    setIsHtmlMode(false);
    setFormData({
      title: item.title || '',
      slug: item.slug || '',
      content: item.content || '',
      is_published: item.is_published !== undefined ? !!item.is_published : (item.is_active !== undefined ? !!item.is_active : true)
    });
    setShowFormModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'slug') {
      setSlugManuallyEdited(true);
      setFormData(prev => ({ ...prev, slug: value }));
    } else if (name === 'title') {
      setFormData(prev => ({
        ...prev,
        title: value,
        ...((!slugManuallyEdited) && { slug: toSlug(value) })
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('auth_token');
      // For file uploads with potentially PUT logic, typically we use POST and append _method
      const method = 'POST';
      const url = editingItem 
        ? `${API_URL}/api/admin/blogs/${editingItem.id}`
        : `${API_URL}/api/admin/blogs`;

      const payload = new FormData();
      payload.append('title', formData.title);
      payload.append('slug', formData.slug);
      payload.append('content', formData.content);
      payload.append('is_published', formData.is_published ? 1 : 0);
      if (imageFile) {
        payload.append('image', imageFile);
      }
      if (editingItem) {
        payload.append('_method', 'PUT');
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: payload
      });

      const res = await response.json();
      if (!response.ok) throw res;

      toast.success(editingItem ? 'Blog updated!' : 'Blog created!');
      fetchBlogs();
      setShowFormModal(false);
    } catch (err) {
      toast.error(err.message || 'Failed to save blog. (API might not exist yet)');
    }
  };

  const executeDelete = async () => {
    if (!itemToDelete) return;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/blogs/${itemToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error('Failed to delete');
      setBlogs(blogs.filter(b => b.id !== itemToDelete.id));
      setItemToDelete(null);
      toast.success('Blog deleted.');
    } catch (err) {
      toast.error('Error deleting blog. (API might not exist yet)');
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .bl-wrap { font-family: 'Inter', sans-serif; }

        /* Quill overrides */
        .ql-container { font-family: 'Inter', sans-serif !important; font-size: 15px !important; }
        .ql-editor { min-height: 350px; }
        .ql-toolbar.ql-snow { border: none !important; border-bottom: 1px solid #dde3ea !important; background: #f9fbfc; border-radius: 8px 8px 0 0; }
        .ql-container.ql-snow { border: none !important; }

        /* Hero Banner */
        .bl-hero {
          background: linear-gradient(135deg, #040f1f 0%, #061830 40%, #CFA047 100%);
          border-radius: 18px; padding: 32px 36px; margin-bottom: 28px;
          display: flex; align-items: center; justify-content: space-between;
          position: relative; overflow: hidden;
        }
        .bl-hero::before { content: ''; position: absolute; top: -40px; right: -40px; width: 240px; height: 240px; border-radius: 50%; background: rgba(255,255,255,0.05); }
        .bl-hero::after { content: ''; position: absolute; bottom: -60px; right: 120px; width: 180px; height: 180px; border-radius: 50%; background: rgba(255,255,255,0.04); }
        .bl-hero-left { position: relative; z-index: 1; }
        .bl-hero-eyebrow { font-size: 11px; font-weight: 700; color: #CFA047; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
        .bl-hero-title { font-size: 26px; font-weight: 800; color: #ffffff; margin: 0 0 8px; line-height: 1.2; }
        .bl-hero-sub { font-size: 14px; color: rgba(255,255,255,0.8); margin: 0; }
        .bl-hero-right { position: relative; z-index: 1; display: flex; align-items: center; gap: 16px; }
        .bl-hero-stat { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.15); border-radius: 14px; padding: 16px 22px; text-align: center; backdrop-filter: blur(6px); }
        .bl-hero-stat-val { font-size: 28px; font-weight: 800; color: #ffffff; line-height: 1; }
        .bl-hero-stat-label { font-size: 11px; color: #CFA047; font-weight: 600; margin-top: 4px; }
        .bl-create-btn { display: flex; align-items: center; gap: 8px; padding: 11px 22px; border-radius: 10px; border: none; background: #ffffff; color: #061830; font-size: 14px; font-weight: 700; cursor: pointer; font-family: 'Inter', sans-serif; box-shadow: 0 4px 14px rgba(0,0,0,0.15); transition: all 0.2s; white-space: nowrap; }
        .bl-create-btn:hover { box-shadow: 0 6px 18px rgba(207, 160, 71,0.4); transform: translateY(-1px); }

        /* Error */
        .bl-error { background: #fff1f2; border: 1px solid #fecdd3; border-radius: 10px; padding: 12px 16px; color: #e11d48; font-size: 13px; margin-bottom: 20px; display: flex; align-items: center; gap: 10px; }

        /* Table card */
        .bl-table-card { background: #ffffff; border-radius: 14px; border: 1px solid #dde3ea; box-shadow: 0 2px 12px rgba(0,0,0,0.05); overflow: hidden; }
        .bl-table-toolbar { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid #eef1f5; }
        .bl-table-toolbar-title { font-size: 14px; font-weight: 600; color: #2d3a4b; }
        .bl-count-badge { background: #f0f4f8; color: #5a6a7e; font-size: 12px; font-weight: 600; padding: 3px 10px; border-radius: 20px; margin-left: 8px; }
        table.bl-table { width: 100%; border-collapse: collapse; }
        .bl-table thead tr { background: #f7f9fc; }
        .bl-table th { padding: 13px 18px; font-size: 11.5px; font-weight: 700; color: #8496a9; text-transform: uppercase; letter-spacing: 0.6px; border-bottom: 1px solid #eef1f5; }
        .bl-table td { padding: 15px 18px; font-size: 13.5px; color: #3d4f62; border-bottom: 1px solid #f0f4f8; vertical-align: middle; }
        .bl-table tbody tr:last-child td { border-bottom: none; }
        .bl-table tbody tr:hover td { background: #f7f9fc; }

        .bl-badge { display: inline-flex; align-items: center; gap: 5px; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }
        .bl-badge.active { background: #f8fafc; color: #061830; }
        .bl-badge.inactive { background: #f8fafc; color: #94a3b8; }
        
        .bl-actions { display: flex; gap: 6px; }
        .bl-act-btn { display: flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 8px; border: 1.5px solid #dde3ea; background: #f7f9fc; color: #6b7a90; cursor: pointer; font-size: 13px; transition: all 0.18s; }
        .bl-act-btn:hover { background: #fff; border-color: #bcc7d4; color: #1a2332; }
        .bl-act-btn.edit:hover { border-color: #CFA047; color: #CFA047; background: #f8fafc; }
        .bl-act-btn.del:hover { border-color: #fca5a5; color: #ef4444; background: #fff1f2; }

        /* Slide-in Drawer */
        .bl-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); backdrop-filter: blur(4px); z-index: 900; display: flex; justify-content: flex-end; }
        @keyframes bl-slide { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .bl-drawer { width: 780px; max-width: 95vw; background: #fff; height: 100vh; display: flex; flex-direction: column; box-shadow: -20px 0 60px rgba(0,0,0,0.14); animation: bl-slide 0.28s cubic-bezier(0.16,1,0.3,1); }
        .bl-drawer-hd { padding: 22px 28px; border-bottom: 1px solid #eef1f5; display: flex; align-items: center; justify-content: space-between; background: linear-gradient(135deg, #061830, #040f1f); flex-shrink: 0; }
        .bl-drawer-hd-left { display: flex; align-items: center; gap: 14px; }
        .bl-drawer-icon { width: 40px; height: 40px; border-radius: 12px; background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25); display: flex; align-items: center; justify-content: center; color: #CFA047; font-size: 16px; }
        .bl-drawer-title { font-size: 17px; font-weight: 700; color: #fff; margin: 0; }
        .bl-drawer-subtitle { font-size: 12px; color: rgba(255,255,255,0.8); margin-top: 2px; }
        .bl-drawer-close { background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.2); color: white; width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 14px; transition: background 0.18s; }
        .bl-drawer-close:hover { background: rgba(255,255,255,0.22); }

        /* Drawer Body: two-panel layout */
        .bl-drawer-body { display: flex; flex: 1; overflow: hidden; }

        /* Left: image preview panel */
        .bl-img-panel { width: 280px; flex-shrink: 0; background: #f7f9fc; border-right: 1px solid #eef1f5; display: flex; flex-direction: column; overflow: hidden; }
        .bl-img-banner { flex: 1; position: relative; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .bl-img-banner img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .bl-img-banner-placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; color: #cbd5e1; padding: 20px; text-align: center; height: 100%; }
        .bl-img-banner-placeholder i { font-size: 52px; color: #faedcf; }
        .bl-img-banner-placeholder span { font-size: 13px; color: #9babbf; line-height: 1.5; }
        .bl-upload-overlay { position: absolute; inset: 0; background: rgba(207, 160, 71,0.88); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; opacity: 0; transition: opacity 0.2s; cursor: pointer; }
        .bl-img-banner:hover .bl-upload-overlay { opacity: 1; }
        .bl-upload-overlay i { font-size: 28px; color: white; }
        .bl-upload-overlay span { font-size: 13px; color: white; font-weight: 600; }
        .bl-upload-overlay input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
        .bl-img-info { padding: 14px 16px; border-top: 1px solid #eef1f5; }
        .bl-img-info-title { font-size: 11px; font-weight: 700; color: #8496a9; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px; }
        .bl-toggle-row { display: flex; align-items: center; justify-content: space-between; }
        .bl-toggle-label { font-size: 13px; font-weight: 500; color: #3d4f62; }

        .bl-form-panel { flex: 1; overflow-y: auto; padding: 28px; display: flex; flex-direction: column; gap: 22px; }

        .bl-section-title { font-size: 11px; font-weight: 700; color: #CFA047; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 14px; display: flex; align-items: center; gap: 8px; }
        .bl-section-title::after { content: ''; flex: 1; height: 1px; background: #fdf5e6; }

        .bl-float-group { position: relative; margin-bottom: 6px; }
        .bl-float-inp { width: 100%; padding: 20px 16px 8px; border-radius: 10px; border: 1.5px solid #dde3ea; font-size: 14px; font-family: 'Inter', sans-serif; color: #2d3a4b; outline: none; background: #f9fbfc; transition: border-color 0.18s, box-shadow 0.18s; box-sizing: border-box; }
        .bl-float-inp:focus { border-color: #CFA047; box-shadow: 0 0 0 3px rgba(207, 160, 71,0.1); background: #fff; }
        .bl-float-inp:focus + .bl-float-label, .bl-float-inp:not(:placeholder-shown) + .bl-float-label { top: 7px; font-size: 10.5px; color: #CFA047; font-weight: 700; }
        .bl-float-label { position: absolute; top: 14px; left: 16px; font-size: 13px; color: #9babbf; font-weight: 500; pointer-events: none; transition: all 0.18s; }
        
        .bl-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

        .bl-switch-row { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; background: #f9fbfc; border: 1.5px solid #dde3ea; border-radius: 10px; }
        .bl-switch-label { font-size: 13px; font-weight: 600; color: #2d3a4b; }
        .bl-switch-sub { font-size: 11px; color: #9babbf; margin-top: 2px; }
        .bl-switch { position: relative; width: 40px; height: 24px; flex-shrink: 0; }
        .bl-switch input { opacity: 0; width: 0; height: 0; }
        .bl-slider { position: absolute; inset: 0; background: #dde3ea; border-radius: 50px; cursor: pointer; transition: 0.2s; }
        .bl-slider:before { content: ''; position: absolute; width: 18px; height: 18px; left: 3px; top: 3px; background: white; border-radius: 50%; transition: 0.2s; box-shadow: 0 1px 4px rgba(0,0,0,0.18); }
        .bl-switch input:checked + .bl-slider { background: #CFA047; }
        .bl-switch input:checked + .bl-slider:before { transform: translateX(16px); }

        .bl-df { padding: 18px 28px; border-top: 1px solid #eef1f5; display: flex; justify-content: space-between; align-items: center; background: #f9fbfc; flex-shrink: 0; }
        .bl-df-hint { font-size: 12px; color: #9babbf; }
        .bl-df-btns { display: flex; gap: 12px; }
        .bl-btn-cancel { padding: 10px 22px; border-radius: 9px; font-weight: 600; cursor: pointer; background: white; border: 1.5px solid #dde3ea; color: #4a5568; font-family: 'Inter', sans-serif; font-size: 13px; transition: all 0.18s; }
        .bl-btn-cancel:hover { background: #f7f9fc; }
        .bl-btn-save { padding: 10px 26px; border-radius: 9px; font-weight: 600; cursor: pointer; background: linear-gradient(135deg, #CFA047 0%, #061830 100%); border: none; color: white; font-family: 'Inter', sans-serif; font-size: 13px; box-shadow: 0 2px 8px rgba(207, 160, 71,0.3); transition: all 0.18s; display: flex; align-items: center; gap: 8px; }
        .bl-btn-save:hover { box-shadow: 0 4px 14px rgba(207, 160, 71,0.4); transform: translateY(-1px); }

        .bl-del-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.6); backdrop-filter: blur(4px); display: flex; justify-content: center; align-items: center; z-index: 999; }
        @keyframes bl-slide-up { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>

      <div className="bl-wrap">
        {/* Hero Banner */}
        <div className="bl-hero">
          <div className="bl-hero-left">
            <div className="bl-hero-eyebrow"><i className="fa-solid fa-blog"></i> Blog Management</div>
            <h2 className="bl-hero-title">Manage Blogs</h2>
            <p className="bl-hero-sub">Create, edit, and publish blogs to your website.</p>
          </div>
          <div className="bl-hero-right">
            {!loading && (
              <>
                <div className="bl-hero-stat">
                  <div className="bl-hero-stat-val">{blogs.length}</div>
                  <div className="bl-hero-stat-label">Total</div>
                </div>
                <div className="bl-hero-stat">
                  <div className="bl-hero-stat-val">{blogs.filter(b => b.is_published || b.is_active).length}</div>
                  <div className="bl-hero-stat-label">Published</div>
                </div>
              </>
            )}
            <button className="bl-create-btn" onClick={handleOpenCreate}>
              <i className="fa-solid fa-plus"></i> Create Blog
            </button>
          </div>
        </div>

        {error && <div className="bl-error"><i className="fa-solid fa-circle-exclamation"></i>{error}</div>}

        <div className="bl-table-card">
          <div className="bl-table-toolbar">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span className="bl-table-toolbar-title">All Blogs</span>
              <span className="bl-count-badge">{blogs.length}</span>
            </div>
          </div>
          <table className="bl-table">
            <thead>
              <tr>
                <th>Blog</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="3" style={{ textAlign: 'center', padding: '40px', color: '#9babbf' }}>Loading blogs...</td></tr>
              ) : blogs.length === 0 ? (
                <tr><td colSpan="3" style={{ textAlign: 'center', padding: '60px', color: '#9babbf' }}>No blogs yet.</td></tr>
              ) : (
                blogs.map(item => (
                  <tr key={item.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <img src={item.image || item.img || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=80&q=80'} alt={item.title} style={{ width: '60px', height: '40px', borderRadius: '6px', objectFit: 'cover', border: '1px solid #dde3ea' }} />
                        <div>
                          <div style={{ fontWeight: 600, color: '#1a2332' }}>{item.title}</div>
                          {item.slug && <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>/{item.slug}</div>}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`bl-badge ${item.is_published || item.is_active ? 'active' : 'inactive'}`}>
                        {(item.is_published || item.is_active) ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td>
                      <div className="bl-actions" style={{ justifyContent: 'flex-end' }}>
                        <button className="bl-act-btn edit" onClick={() => handleOpenEdit(item)} title="Edit"><i className="fa-solid fa-pen-to-square"></i></button>
                        <button className="bl-act-btn del" onClick={() => setItemToDelete(item)} title="Delete"><i className="fa-solid fa-trash-can"></i></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Slide-in Drawer */}
      {showFormModal && (
        <div className="bl-overlay" onClick={() => setShowFormModal(false)}>
          <div className="bl-drawer" onClick={e => e.stopPropagation()}>
            <div className="bl-drawer-hd">
              <div className="bl-drawer-hd-left">
                <div className="bl-drawer-icon"><i className="fa-solid fa-blog"></i></div>
                <div>
                  <div className="bl-drawer-title">{editingItem ? 'Edit Blog' : 'Create New Blog'}</div>
                  <div className="bl-drawer-subtitle">{editingItem ? 'Update blog details' : 'Write a new blog post'}</div>
                </div>
              </div>
              <button className="bl-drawer-close" onClick={() => setShowFormModal(false)}><i className="fa-solid fa-xmark"></i></button>
            </div>

            <form onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              <div className="bl-drawer-body">
                {/* Left: Live Image Preview */}
                <div className="bl-img-panel">
                  <div className="bl-img-banner">
                    {imagePreview
                      ? <img src={imagePreview} alt="Preview" />
                      : (
                        <div className="bl-img-banner-placeholder">
                          <i className="fa-solid fa-image"></i>
                          <span>Hover to upload a blog cover image.</span>
                        </div>
                      )
                    }
                    <div className="bl-upload-overlay">
                      <i className="fa-solid fa-camera"></i>
                      <span>{imagePreview ? 'Change Image' : 'Upload Image'}</span>
                      <input type="file" accept="image/*" onChange={handleImageChange} />
                    </div>
                  </div>
                  <div className="bl-img-info">
                    <div className="bl-img-info-title">Visibility</div>
                    <div className="bl-toggle-row" style={{ padding: '0', border: 'none', background: 'transparent' }}>
                      <span className="bl-toggle-label">Published</span>
                      <label className="bl-switch">
                        <input type="checkbox" name="is_published" checked={formData.is_published} onChange={handleInputChange} />
                        <span className="bl-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Right: Form Fields */}
                <div className="bl-form-panel">
                  <div className="bl-section-title"><i className="fa-solid fa-info-circle"></i> Blog Details</div>
                  
                  <div className="bl-row">
                    <div className="bl-float-group">
                      <input className="bl-float-inp" type="text" name="title" value={formData.title} onChange={handleInputChange} placeholder=" " required />
                      <label className="bl-float-label">Blog Title *</label>
                    </div>
                    <div>
                      <div className="bl-float-group">
                        <input className="bl-float-inp" type="text" name="slug" value={formData.slug} onChange={handleInputChange} placeholder=" " />
                        <label className="bl-float-label">URL Slug</label>
                      </div>
                      <div className="bl-df-hint" style={{ marginTop: '4px', paddingLeft: '4px' }}>Auto-generated if blank.</div>
                    </div>
                  </div>

                  <div className="bl-float-group" style={{ marginTop: '12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: '#8496a9' }}>Blog Content *</label>
                      <button 
                        type="button" 
                        onClick={() => setIsHtmlMode(!isHtmlMode)}
                        style={{ 
                          background: isHtmlMode ? '#1e293b' : '#f8fafc', 
                          color: isHtmlMode ? '#fff' : '#475569', 
                          border: '1px solid #dde3ea', 
                          padding: '6px 12px', 
                          borderRadius: '6px', 
                          fontSize: '11.5px', 
                          fontWeight: '600', 
                          cursor: 'pointer', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px',
                          transition: 'all 0.2s'
                        }}
                      >
                        {isHtmlMode ? <><i className="fa-solid fa-eye" style={{ color: '#CFA047' }}></i> Visual View</> : <><i className="fa-solid fa-code" style={{ color: '#64748b' }}></i> HTML Source</>}
                      </button>
                    </div>
                    
                    <div style={{ background: isHtmlMode ? '#0f172a' : '#fff', borderRadius: '10px', border: '1.5px solid #dde3ea', overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
                      {isHtmlMode ? (
                        <>
                          <div style={{ padding: '6px 16px', background: '#1e293b', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }}></span>
                            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }}></span>
                            <span style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }}></span>
                            <span style={{ fontSize: 11, color: '#64748b', marginLeft: 8 }}>index.html</span>
                          </div>
                          <textarea
                            value={formData.content}
                            onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                            style={{ flex: 1, width: '100%', padding: '20px', fontFamily: '"Fira Code", Consolas, monospace', fontSize: '13.5px', border: 'none', outline: 'none', resize: 'vertical', background: 'transparent', color: '#e2e8f0', lineHeight: '1.7', minHeight: '350px', tabSize: 2 }}
                            placeholder="<p>Write your raw HTML here...</p>"
                            onKeyDown={(e) => {
                              if (e.key === 'Tab') {
                                e.preventDefault();
                                const s = e.target.selectionStart, en = e.target.selectionEnd;
                                const v = formData.content.substring(0, s) + '  ' + formData.content.substring(en);
                                setFormData(prev => ({ ...prev, content: v }));
                                setTimeout(() => { e.target.selectionStart = e.target.selectionEnd = s + 2; }, 0);
                              }
                            }}
                          />
                        </>
                      ) : (
                        <ReactQuill
                          theme="snow"
                          modules={quillModules}
                          value={formData.content}
                          onChange={(val) => setFormData(prev => ({ ...prev, content: val }))}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bl-df">
                <span className="bl-df-hint"><i className="fa-solid fa-asterisk" style={{ fontSize: '9px', verticalAlign: 'middle' }}></i> Required fields</span>
                <div className="bl-df-btns">
                  <button type="button" className="bl-btn-cancel" onClick={() => setShowFormModal(false)}>Cancel</button>
                  <button type="submit" className="bl-btn-save"><i className="fa-solid fa-floppy-disk"></i> {editingItem ? 'Update Blog' : 'Save Blog'}</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {itemToDelete && (
        <div className="bl-del-overlay" onClick={() => setItemToDelete(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', padding: '28px 24px', borderRadius: '16px', width: '320px', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', animation: 'bl-slide-up 0.2s ease-out' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#fee2e2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', margin: '0 auto 16px' }}>
              <i className="fa-solid fa-triangle-exclamation"></i>
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>Delete Blog</h3>
            <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 24px', lineHeight: 1.5 }}>
              Are you sure you want to delete the blog <strong>"{itemToDelete.title}"</strong>? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button style={{ flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#f1f5f9', color: '#475569' }} onClick={() => setItemToDelete(null)}>Cancel</button>
              <button style={{ flex: 1, padding: '12px', borderRadius: '10px', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#ef4444', color: 'white' }} onClick={executeDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminBlogs;
